import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

// Assets
import { DotsVertical, Edit02 } from "@untitled-ui/icons-react";

// API related
import { IDirectoryUser } from "#src/services/endpoint/directory/types";
import { TableV2 } from "#src/commonV2/Table";
import { TableActionLabelV2 } from "#src/commonV2/Table/TableActionLabel";

// Components
import { Dropdown, Tooltip } from "@lockerpm/design";
import { DropdownRenderV2 } from "#src/commonV2/antdHelpers/DropdownRender";
import { BadgeSeeMoreV2 } from "#src/commonV2/badges/BadgeSeeMore";
import { BadgeV2 } from "#src/commonV2/badges/Badge";

type Props = {
  userList: IDirectoryUser[];
  isLoading: boolean;
  selectedUserIds: string[];
  setSelectedUserIds: (ids: string[]) => void;
  setSelectedPage: (page: number) => void;
  setPageSize: (size: number) => void;
  userCount: number;
  sortOrder: string;
  setSortOrder: (order: string) => void;
  selectedPage: number;
  pageSize: number;
  openEditUserModal: (id: string) => void;
};

export const UsersTable = (props: Props) => {
  const {
    userList,
    isLoading,
    selectedUserIds,
    setSelectedPage,
    setPageSize,
    userCount,
    sortOrder,
    setSortOrder,
    selectedPage,
    pageSize,
    openEditUserModal,
  } = props;
  const { t } = useTranslation("endpoint", { keyPrefix: "directory" });

  // --------------- METHODS ---------------

  const onChangePage = (page: number) => {
    setSelectedPage(page);
  };

  const onChangePageSize = (size: number) => {
    setPageSize(size);
    setSelectedPage(1);
  };

  // Action list for kebab menu
  const getTableActions = (record: IDirectoryUser) => {
    const actions: {
      key: string;
      label: ReactNode;
      action: () => void;
    }[] = [
      {
        key: "edit",
        label: (
          <TableActionLabelV2
            icon={<Edit02 className="h-5 w-5" />}
            text={t("table.actions.edit")}
          />
        ),
        action: () => {
          openEditUserModal(record.id);
        },
      },
    ];

    return actions;
  };

  // Render devices label
  const renderDevicesLabel = (devices: { id: number; name: string }[]) => {
    if (devices.length === 0) {
      return "---";
    }

    return (
      <div className="flex flex-wrap gap-1">
        {/* < 3 labels */}
        {devices.slice(0, 3).map((device) => (
          <BadgeV2 key={device.id} name={device.name} color="brand" />
        ))}
        {/* < 3 labels end */}

        {/* > 3 labels */}
        {devices.length > 3 && (
          <BadgeSeeMoreV2
            badges={devices.slice(3).map((item) => (
              <BadgeV2 key={item.id} name={item.name} color="brand" />
            ))}
          />
        )}
        {/* > 3 labels end */}
      </div>
    );
  };

  // --------------- RENDER ---------------

  return (
    <TableV2<IDirectoryUser>
      data={userList}
      getRowKey={(item) => item.id}
      isLoading={isLoading}
      columns={[
        // Name
        {
          title:
            selectedUserIds.length === 0 ? (
              t("table.columns.name")
            ) : (
              <span className="text-brand-v2">
                {t("table.selected", { count: selectedUserIds.length })}
              </span>
            ),
          render: (item) => (
            <Tooltip
              title={<div className="text-center text-xs">{item.email}</div>}
            >
              {item.full_name}
            </Tooltip>
          ),
          overwriteWrapperProps: {
            className: "font-medium text-gray-v2-900",
          },
          gridTemplateValue: "1fr",
          sortingKey: { asc: "name_asc", desc: "name_desc" },
        },

        // Department
        {
          title: t("table.columns.department"),
          render: (item) => item.department?.name || "---",
          gridTemplateValue: "1fr",
        },

        // Title
        {
          title: t("table.columns.title"),
          render: (item) => item.job_title,
          gridTemplateValue: "1fr",
        },

        // Devices
        {
          title: t("table.columns.devices"),
          render: (item) => renderDevicesLabel(item.devices),
          gridTemplateValue: "2fr",
        },

        // Added At
        {
          title: t("table.columns.added_at"),
          render: (item) =>
            item.created_time
              ? DateTime.fromSeconds(item.created_time).toFormat("dd/MM/yyyy")
              : "---",
          gridTemplateValue: "1fr",
        },

        // Actions
        {
          title: null,
          render: (item) => {
            const actions = getTableActions(item);

            return (
              <Dropdown
                menu={{
                  items: actions,
                  onClick: ({ key, domEvent }) => {
                    domEvent.preventDefault();
                    domEvent.stopPropagation();
                    actions.find((action) => action.key === key)?.action();
                  },
                }}
                dropdownRender={(menu) => (
                  <DropdownRenderV2>{menu}</DropdownRenderV2>
                )}
                trigger={["click"]}
                disabled={selectedUserIds.length > 0}
              >
                <button>
                  <DotsVertical
                    className={`h-5 w-5 ${selectedUserIds.length > 0 ? "text-gray-v2-200" : "text-gray-v2-400"} transition-colors duration-300`}
                  />
                </button>
              </Dropdown>
            );
          },
          overwriteWrapperProps: { className: "justify-center" },
          gridTemplateValue: "1fr",
        },
      ]}
      sort={{
        value: sortOrder,
        onChange: (value) => {
          setSortOrder(value);
        },
      }}
      pagination={{
        count: userCount,
        selected: selectedPage,
        onChange: onChangePage,
        size: { value: pageSize, onChange: onChangePageSize },
      }}
      options={{
        fitPageHeight: true,
      }}
    />
  );
};
